// features/standalone-versions/standalone-version.ts
//
// A VERSION MADE ON ITS OWN, with no story of the user's behind it.
//
// A version is always a version OF a page: the schema requires it, the editor
// opens one at /pages/editor/:storyId/:versionId, analytics and share links
// group by page. So "create a version, not a story" is done by giving each
// user one page nobody sees -- their HOST page, marked hostUserId on the
// server (dotell-backend pages/host-page.js) -- and hanging the version off
// it like any other. The host is hidden from every story list; the version
// is an ordinary version everywhere else.
//
// Who wants this: a Creator lite cannot create stories at all, so this is the
// only way they get a document of their own; a creator may simply prefer a
// version -- the deck as it goes out to someone -- to a story it would have
// to be cut from.
//
// The pure parts live here; ./use-create-standalone-version does the network.

import type { StoryType } from "@config";
import type { TAccount, TPage } from "@/shared/services";
import { DEFAULT_FAVICON } from "@/shared/constants";
import { withDocumentInfoOnFirstSlide } from "@/shared/data/document-info-element";
import { isNormalizedNestedStructure, normalizeToNestedData } from "@/shared";

/** What "Create new" makes: a story of the user's own, or a standalone version. */
export type CreateTarget = "story" | "version";

/**
 * Where a standalone version came from, kept on its record so the Versions
 * page can say so where a story's version names its story.
 */
export type VersionOrigin = {
  kind: "template" | "slides" | "ai" | "blank";
  /** The template's name, when it came from one. */
  title?: string;
};

/** The origin on a version's record, or null for a version that has none. */
export const versionOrigin = (version: {
  data?: Record<string, unknown> | null;
}): VersionOrigin | null => {
  const origin = version.data?.origin as Partial<VersionOrigin> | undefined;
  if (!origin || typeof origin !== "object") return null;
  if (!["template", "slides", "ai", "blank"].includes(String(origin.kind))) {
    return null;
  }
  return {
    kind: origin.kind as VersionOrigin["kind"],
    ...(typeof origin.title === "string" && origin.title.trim()
      ? { title: origin.title.trim() }
      : {}),
  };
};

/**
 * A version made through "Create new", whether it hangs off an org story or
 * the user's host page: every one carries an origin, and a version cut from
 * a story never does. These go settings-first on Update & Share -- title and
 * variables, then the success screen.
 */
export const isCreatedVersion = (
  version: { data?: Record<string, unknown> | null } | null | undefined,
): boolean => Boolean(version && versionOrigin(version));

/**
 * A version that has been created but never finished: its settings and
 * variables are still the ones it was born with, so "Share" belongs at the
 * settings step rather than at a link. Only a created version can be in this
 * state -- an ordinary version is shared the moment it is made.
 */
export const isPendingShareVersion = (
  version: { data?: Record<string, unknown> | null } | null | undefined,
): boolean => isCreatedVersion(version) && version?.data?.pendingShare === true;

/**
 * A version made on its own, off the user's host page -- marked so on its
 * record. A version made from one of the org's stories or versions hangs off
 * that story instead and is an ordinary version of it, origin and all.
 */
export const isStandaloneVersion = (
  version: { data?: Record<string, unknown> | null } | null | undefined,
): boolean => version?.data?.standalone === true;

/**
 * The version's own variable list, as JSON -- the counterpart of a story's
 * settings.fs["fs-vars"], kept on the version because its page, the host, has
 * none. Null when the version carries no list.
 */
export const standaloneVersionVars = (
  version: { data?: Record<string, unknown> | null } | null | undefined,
): string | null => {
  const fsVars = version?.data?.fsVars;
  return typeof fsVars === "string" && fsVars ? fsVars : null;
};

/**
 * What a card says about the origin: the template's name, or "Created from
 * slides". Nothing for a deck written by AI or started blank.
 */
export const versionOriginLabel = (
  origin: VersionOrigin | null,
): string | null => {
  if (!origin) return null;
  if (origin.kind === "template")
    return origin.title || "Created from template";
  if (origin.kind === "slides") return "Created from slides";
  return null;
};

export const DEFAULT_VERSION_TITLE = "Untitled version";

const DEFAULT_OG_IMAGE =
  "https://www.storydoc.com/assets/images/branding/og-storydoc.gif";

/** The page a user's standalone versions hang off; never a story to list. */
export const isHostPage = (
  page: { hostUserId?: string | null } | null | undefined,
): boolean => Boolean(page?.hostUserId);

/** Where a new version opens, ready to be named. */
export const standaloneVersionEditorPath = (
  pageId: string,
  versionId: string,
): string => `/pages/editor/${pageId}/${versionId}?autosave=true&focusTitle=1`;

/**
 * The version's own record: its name, where it came from, the variables its
 * content uses (the list a story keeps at publish time; here the version's,
 * since its page has none) and the reader page's Open Graph image. Nothing
 * gated, no password, no PDF -- the share dialog can add those later, as for
 * any version.
 */
export const standaloneVersionData = (
  title: string,
  origin: VersionOrigin,
  options: { fsVars?: string; ogImage?: string; standalone?: boolean } = {},
): Record<string, unknown> => ({
  title,
  ogImage: options.ogImage || DEFAULT_OG_IMAGE,
  password: null,
  origin,
  // MADE, NOT YET SHARED. The version exists from the moment it is created --
  // it has a link and a record -- but its settings and its variables have not
  // been through "Update & Share", which is the step that finishes it. Until
  // then, asking to share it anywhere should lead back to that step; see
  // isPendingShareVersion, cleared by the settings dialog on its way out.
  pendingShare: true,
  ...(options.fsVars ? { fsVars: options.fsVars } : {}),
  ...(options.standalone ? { standalone: true } : {}),
});

type StoryInput = {
  /** The base: a template, a picked document, a blank story. */
  template: StoryType;
  /**
   * The page the version hangs off: the org story it was made from, or the
   * caller's hidden host page when there is none.
   */
  page: TPage;
  title: string;
  /** The account's theme, when the version should not keep the template's own. */
  designSystem?: Record<string, string>;
  customColors?: string[];
  account?: TAccount | null;
};

/**
 * The version's content as a full story JSON -- the shape every content
 * pointer holds and the editor reads back.
 *
 * Filed under its page: its id, org and canonical, since the reader URL and
 * the copy count both go by the page. Named after the version, on the record
 * and on the reader-facing site name. The slides are the template's, brought
 * to the nested shape the editor expects, with the Document info pills on
 * the first, as every new document gets.
 */
export const standaloneVersionStory = ({
  template,
  page,
  title,
  designSystem,
  customColors,
  account,
}: StoryInput): StoryType => {
  const slides = template.slides?.length
    ? isNormalizedNestedStructure(template.slides)
      ? (template.slides as Record<string, unknown>[])
      : normalizeToNestedData(template.slides as Record<string, unknown>[])
    : [];
  const templateSettings = (template.settings ?? {}) as Record<string, unknown>;
  const og = (templateSettings.og ?? {}) as Record<string, unknown>;
  const settings: Record<string, unknown> = {
    ...templateSettings,
    title,
    canonical: page.settings?.canonical,
    contentPointer: null,
    publicContentPointer: null,
    og: { ...og, siteName: title },
    favicon: templateSettings.favicon || account?.favicon || DEFAULT_FAVICON,
  };
  if (designSystem) settings.designSystemV2 = designSystem;
  if (customColors) settings.customColors = customColors;
  if (account) {
    settings.storydocBranding = !account.hidePoweredByStorydoc;
    if (account.defaultLanguage) settings.language = account.defaultLanguage;
  }
  return {
    ...template,
    _id: page._id,
    orgId: page.orgId,
    folderId: page.folderId ?? null,
    status: "Live",
    layoutVersion: window.BUNDLE_VERSION || page.layoutVersion || "5.0.0",
    history: {},
    slides: withDocumentInfoOnFirstSlide(slides),
    settings,
  } as unknown as StoryType;
};
